<template>
  <div id="gameWrapper" class="Game">
    <TimerBar v-if="showTimerBar" :full="isMTC ? gameTime / 6000 : gameTime / 60000" />
    <TopBar
      :show-text-bar="false"
      :show-say-word="false"
      :show-number-score="true"
      :hide-pause="$store.state.user == null || isMTC"
      :show-end-game="isMTC"
      :score="$store.state.user ? Math.round(score) : null"
      :answers-given="answersGiven.length > 0 ? answersGiven : null"
      @pause="didPressPauseButton"
      @endgame="didPressEndGame"
    />
    <div v-if="!$store.state.user && (!started || showResult)" class="has-text-centered" style="padding: 0 1rem;">
      <router-link :to="'/' + $i18n.locale + '/login'">
        <img class="image" style="max-height: 64px; margin: 0 auto;" src="/images/MathsShedLogo.png">
      </router-link>
    </div>
    <div v-if="!started " class="section">
      <div class="columns">
        <div class="column is-6 is-offset-3">
          <div class="box content resultBox">
            <h2 v-if="isMTC">
              Year 4 MTC Simulator
            </h2>
            <h2 v-else>
              Start Game
            </h2>
            <p v-if="isMTC">
              Answer {{ MTCQuestionCount }} questions in no more than six seconds each. After six seconds the answer displayed will be submitted automatically. A three second pause will be given between questions.
            </p>
            <p v-else>
              Press play to start the game. Answer as many questions as you can in 1 minute.
            </p>
            <a href="#" class="playButton" @click.prevent="startGame"><img src="/images/playButton.png"></a>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showResult" class="section">
      <div class="columns">
        <div class="column is-6 is-offset-3">
          <div class="box content resultBox">
            <h2>Game Over</h2>
            <p class="has-text-centered">
              <b><i class="emojiIcon emojiStar" />&nbsp;{{ enforceLatinNumberSystem(score) }}</b>
            </p>
            <p class="has-text-centered">
              {{ correctAnswerCount() }} / {{ totalAnswerCount() }} correct
            </p>
            <p v-if="correctAnswerCount() > 0" class="has-text-centered">
              {{ ((totalTime / 1000) / correctAnswerCount()).toFixed(1) }}s average
            </p>
            <p v-if="$store.state.user" class="has-text-centered">
              🍯 {{ correctAnswerCount() }}
            </p>
            <a href="#" class="playButton" @click.prevent="startGame"><img src="/images/replayButton.png"></a> <a v-if="$store.state.user" href="#" class="playButton" @click.prevent="quit"><img src="/images/quitButton.png"></a>
            <h3>Answers Given</h3>
            <ul class="answersList">
              <li v-for="(ans, index) in answersGiven" :key="`answer-${index}`">
                <span :class="{'correct' : ans.correct === '1', 'incorrect' : ans.correct !== '1'}"><i v-if="ans.correct === '1'" class="fas fa-check" /><i v-else class="fas fa-times" /> {{ ans.question }} = {{ ans.answer }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <NumberQuestionBar v-if="currentQuestion && started && !showResult" :text="currentQuestion.question" :next-text="nextQuestion ? nextQuestion.question : ''" :correct-text="correctText" />
    <NumberAnswersView v-if="currentQuestion && difficulty < 3 && started && !showResult" :answers="currentQuestion.answers" @answerClicked="answered" />
    <NumberCalculatorAnswer
      v-if="currentQuestion && difficulty == 3 && started && !showResult"
      :text="calcText"
      :decimals="questionFactory.decimals"
      :negative="questionFactory.negative"
      :disabled="keysDisabled"
      :invertkeys="isMTC || $store.state.invertCalcOn"
      @answerClicked="answeredCalc"
      @calcTextChanged="calcTextDidChange"
    />
    <SettingsModal
      v-if="showPause"
      :pause="true"
      :number="true"
      location="number"
      :allow-invert-calculator="!isMTC"
      @hide="hideSettingsModal"
    />
    <p v-if="!$store.state.user" class="has-text-white has-text-centered" style="margin-top:10px;">
      Copyright &copy; EdShed {{ (new Date()).getFullYear() }}
    </p>
  </div>
</template>
<script>
import { request, Api, enforceLatinNumberSystem, RateLimitError } from '@/edshed-common/api'
import TimerBar from '@/edshed-common/components/TimerBar'
import TopBar from '@/components/views/TopBar'
import SettingsModal from '@/components/views/SettingsModal'
import NumberQuestionBar from '@/components/views/NumberQuestionBar'
import NumberAnswersView from '@/components/views/NumberAnswers'
import NumberCalculatorAnswer from '@/components/views/NumberCalculatorAnswer'

import FactoryNumberBonds from '@/numberQuestionFactories/numberBonds'
import FactoryTimesTables from '@/numberQuestionFactories/timesTables'
import FactoryPowerTen from '@/numberQuestionFactories/powerTen'
import FactoryAddSubtract from '@/numberQuestionFactories/addSubtract'
import FactoryMore from '@/numberQuestionFactories/more'
import StylingMixin from '@/mixins/Helpers.ts'
import NumberGameHelper from '@/mixins/NumberGameHelper.ts'
import ModalMixin from '@/edshed-common/mixins/ModalMixin'

export default {
  name: 'NumberGame',
  components: {
    TimerBar,
    TopBar,
    SettingsModal,
    NumberQuestionBar,
    NumberAnswersView,
    NumberCalculatorAnswer
  },
  mixins: [StylingMixin, NumberGameHelper, ModalMixin],
  data (router) {
    return {
      difficulty: 1,
      key: '',
      showPause: false,
      started: false,
      showResult: false,
      questionFactory: null,
      currentQuestion: null,
      nextQuestion: null,
      currentAnswers: [],
      score: 0,
      gameTimer: null,
      showIncorrect: false,
      gameTime: 60000,
      questionStartedTime: 60000,
      answersGiven: [],
      calcText: '0',
      keysDisabled: false,
      waitingForNextQustion: false,
      isWaitingToEndMTC: false,
      MTCQuestionCount: 25,
      images: [
        '/images/CalcTickButton.png',
        '/images/CalcButton.png',
        '/images/replayButton.png',
        '/images/playButton.png',
        '/images/answerPlanet.png',
        '/images/numberTagAndBee.png'
      ]
    }
  },
  computed: {
    showTimerBar () {
      if (!this.started || this.showResult) {
        return false
      }

      return !this.$store.getters.disableTimer
    },
    isMTC () {
      return this.$route.meta.mtc
    },
    correctText () {
      if (!this.showIncorrect) {
        return ''
      } else {
        const q = this.currentQuestion.question
        let a = ''
        for (let i = 0; i < this.currentQuestion.answers.length; i++) {
          if (this.currentQuestion.answers[i].correct) {
            a = this.currentQuestion.answers[i].answer
          }
        }
        if (!q.includes('?')) {
          return q + ' = ' + a
        } else {
          return q.replace('?', a)
        }
      }
    },
    totalTime () {
      if (this.isMTC) {
        return this.answersGiven.reduce((a, b) => a + b.time, 0)
        // var t = 0
        // for (var i = 0; i < this.answersGiven.length; i++) {
        //   t += this.answersGiven[i].time
        // }
      } else {
        return 60000
      }
    }
  },
  mounted () {
    this.setTheme('number')
    if (!this.isMTC) {
      this.tick()
    }
    this.$nextTick(() => {
      if (this.isMTC) {
        console.log('IS MTC')
        // this.generateQuestions()
      }
    })
  },
  beforeDestroy () {
    console.log('BEFORE DESTROY')
    window.clearInterval(this.gameTimer)
    this.gameTimer = null
    window.removeEventListener('keyup', this.didKeyUp)
  },
  created () {
    window.clearInterval(this.gameTimer)
    this.gameTimer = null
    if (!this.$store.getters.hasNumber && !this.isMTC) {
      this.$router.push({ name: 'Menu' })
      return
    }
    if (this.$route.params.difficulty) {
      this.difficulty = parseInt(this.$route.params.difficulty)
      if (!this.difficulty || this.difficulty > 3) {
        this.difficulty = 3
      }
    } else {
      this.difficulty = 3
    }
    if (!this.$route.params.key) {
      if (this.isMTC) {
        this.$route.params.key = 'timesTables_2,3,4,5,6,7,8,9,10,11,12_times_mtc'
      } else {
        this.$router.push({ name: 'NumberMenu' })
        return
      }
    }
    if (this.isMTC) {
      this.gameTime = 6000
    }
    this.key = this.$route.params.key
    this.questionFactory = this.getQuestionFactory(this.key)

    window.addEventListener('keyup', this.didKeyUp)
    this.preloadImages()
  },
  methods: {
    preloadImages () {
      const urls = this.images
      urls.forEach((url) => {
        const img = document.createElement('img')
        img.src = url
      })
    },
    calcTextDidChange (txt) {
      this.calcText = txt
    },
    didKeyUp (event) {
      if (this.keysDisabled) {
        return
      }
      console.log(event.which)
      if (event.which === 27) {
        this.showPause = !this.showPause
      }
      if (event.which === 13) {
        if (!this.started) {
          this.startGame()
        }
      }
    },
    startGame () {
      this.$gtag.event('numbergame', { event_category: 'start', value: this.key, event_label: this.difficulty })

      if (this.isMTC) {
        this.gameTime = 6000
        this.questionStartedTime = 6000
      } else {
        this.gameTime = 60000
        this.questionStartedTime = 60000
      }
      this.currentQuestion = null
      this.nextQuestion = null
      this.currentAnswers = []
      this.score = 0
      this.answersGiven = []
      this.generateQuestions()
      this.started = true
      this.showResult = false
    },
    didPressPauseButton () {
      if (this.$store.state.soundFXOn) {
        this.$sounds.clickSound.play()
      }
      this.showPause = true
      // clearInterval(this.gameTimer)
      // this.gameTimer = null
    },
    async didPressEndGame () {
      if (this.$store.state.soundFXOn) {
        this.$sounds.clickSound.play()
      }
      if (this.isMTC) {
        this.isWaitingToEndMTC = true
        const c = await this.confirm({ title: 'Quit Simulation', message: 'Do you wish to quit? Your progress will not be saved.' })
        if (c) {
          this.quit()
        } else {
          this.isWaitingToEndMTC = false
          if (this.answersGiven.length >= this.MTCQuestionCount) {
            this.endGame()
          }
        }
      }
    },
    hideSettingsModal () {
      if (this.$store.state.soundFXOn) {
        this.$sounds.clickSound.play()
      }
      this.showPause = false
      if (this.started && !this.showResult) {
        // this.startGameTimer()
      }
    },
    answered (ans) {
      if (this.showIncorrect || !this.started || this.showResult || this.waitingForNextQustion) {
        return
      }
      this.waitingForNextQustion = true
      const qTime = this.questionStartedTime - this.gameTime
      this.answersGiven.push({ question: this.currentQuestion.question, answer: ans.answer, correct: (ans.correct ? '1' : '0'), time: qTime })
      if (ans.correct && qTime > 250) { // time limit for cheaters
        this.score = Math.round(this.score + (1000 * this.difficulty) + Math.max(0, (6000 - qTime) / 6) * this.difficulty)
        if (this.$store.state.soundFXOn) {
          this.$sounds.correctSound.play()
        }
        this.generateQuestions()
      } else if (qTime <= 250) {
        if (this.$store.state.soundFXOn) {
          this.$sounds.incorrectSound.play()
        }
        // time error
        this.incorrect()
      } else {
        if (this.$store.state.soundFXOn) {
          this.$sounds.incorrectSound.play()
        }
        // show correct answer
        this.incorrect()
      }
    },
    answeredCalc (ans) {
      if (this.showIncorrect || !this.started || this.showResult || this.waitingForNextQustion) {
        return
      }
      this.waitingForNextQustion = true
      const qTime = this.questionStartedTime - this.gameTime
      // console.log('given: ' + parseFloat(ans))
      // console.log('actual: ' + parseFloat(this.currentQuestion.answers[0].answer.replace(/,/g, '')))
      const isCorrect = parseFloat(ans) === this.currentQuestion.answers[0].answer
      this.answersGiven.push({ question: this.currentQuestion.question, answer: ans, correct: (isCorrect ? '1' : '0'), time: qTime })
      if (isCorrect) {
        this.score = Math.round(this.score + (1000 * this.difficulty) + Math.max(0, (6000 - qTime) / 6) * this.difficulty)
        if (this.$store.state.soundFXOn) {
          this.$sounds.correctSound.play()
        }
        if (!this.isMTC) {
          this.generateQuestions()
        }
      } else {
        if (this.$store.state.soundFXOn) {
          this.$sounds.incorrectSound.play()
        }
        if (!this.isMTC) {
          this.incorrect()
        }
      }
      if (this.isMTC && this.answersGiven.length >= this.MTCQuestionCount) {
        if (!this.isWaitingToEndMTC) {
          this.endGame()
        }
      } else if (this.isMTC) {
        this.pauseMtc()
      }
    },
    incorrect () {
      // start timer
      // show correct answer
      this.showIncorrect = true
      const that = this
      setTimeout(() => {
        that.showIncorrect = false
        if (this.isMTC && this.answersGiven.length >= this.MTCQuestionCount) {
          // Don't know why this case is here because MTC never invokes this method
        } else {
          this.generateQuestions()
        }
      }, 2000)
    },
    pauseMtc () {
      this.keysDisabled = true
      window.clearInterval(this.gameTimer)
      this.gameTimer = null
      // show pause...
      const loadingComponent = this.$buefy.loading.open({
        container: null
      })
      // this.$loader
      this.gameTime = 0
      this.questionStartedTime = 6000
      this.calcText = '0'
      this.currentQuestion.question = '...'
      setTimeout(() => {
        this.generateQuestions()
        loadingComponent.close()
        this.keysDisabled = false
      }, 3000)
    },
    generateQuestions () {
      this.calcText = '0'
      console.log('Getting question')
      if (this.nextQuestion === null) {
        this.currentQuestion = this.questionFactory.getQuestion(this.difficulty === 1 ? 3 : this.difficulty === 2 ? 6 : 1)

        // Avoid rare bug where easy will only produce one answer
        if (this.currentQuestion.answers.length <= 1 && this.difficulty !== 3) {
          this.currentQuestion = this.questionFactory.getQuestion(3)
        }
      } else {
        this.currentQuestion = this.nextQuestion
      }
      if (this.difficulty === 3 && !this.isMTC) {
        this.nextQuestion = this.questionFactory.getQuestion()
      }
      this.questionStartedTime = this.gameTime
      if (this.isMTC) {
        this.gameTime = 6000
        this.questionStartedTime = 6000
      }
      if (!this.gameTimer) {
        this.startGameTimer()
      }
      this.waitingForNextQustion = false
    },
    startGameTimer () {
      this.gameTimer = window.setInterval(this.interval, 20)
    },
    interval () {
      this.gameTime -= 20
      if (this.gameTime <= 0) {
        this.cancelGameTimer()
      }
    },
    cancelGameTimer () {
      window.clearInterval(this.gameTimer)
      this.gameTimer = null
      if (!this.isMTC) {
        this.endGame()
      } else {
        this.mtcTimerEnded()
      }
    },
    mtcTimerEnded () {
      // save answer
      this.answeredCalc(this.calcText)
    },
    endGame () {
      if (!this.showResult) {
        this.saveGameData()
      }
      this.gameTime = 0
      window.clearInterval(this.gameTimer)
      this.gameTimer = null
      this.showResult = true
    },
    saveGameData () {
      if (!this.$store.state.user) {
        return
      }
      // save data here
      const params = { score: this.score, answers: this.answersGiven, key: this.key, difficulty: this.difficulty, type: 'standard' }
      request('put', 'number/gamesession', { data: params })
        .then((response) => {
          const data = response.data
          if (data.error) {
            console.log(data.error)
            // alert(data.error)
          }

          if (data.user) {
            this.$store.commit('SET_USER', data.user)
            this.$store.commit('SET_TOKEN', data.user.token)
            if (window.localStorage) {
              window.localStorage.setItem('user', JSON.stringify(data.user))
              window.localStorage.setItem('token', data.user.token)
            }
          }

          // give read replica a chance to catch up
          setTimeout(async () => {
            const newHw = await Api.getHomework({ id: data.user.id, status: 'active', has_licence: true }, undefined)

            this.$store.commit('SET_HOMEWORKS', newHw)
          }, 300)
        })
        .catch((error) => {
          console.log(error)

          if (error instanceof RateLimitError) {
            const timeDenomination = error.timeToReset > (60 * 60) ? 'hour' : 'minute'
            const timeInUnits = error.timeToReset > (60 * 60) ? Math.ceil(error.timeToReset / (60 * 60)) : Math.ceil(error.timeToReset / 60)

            this.$buefy.dialog.alert({
              title: 'Suspicious Activity Detected',
              message: `We have noticed an unusally high number of games played on your account. Further game plays will be blocked for the next ${timeInUnits} ${timeDenomination}${timeInUnits > 1 ? 's' : ''}.`
            })

            this.quit()
          }

          this.response = 'Details incorrect'
        })
    },
    quit () {
      this.$router.push({ name: 'NumberMenu' })
    },
    correctAnswerCount () {
      let c = 0
      for (let i = 0; i < this.answersGiven.length; i++) {
        if (this.answersGiven[i].correct === '1') {
          c++
        }
      }
      return c
    },
    totalAnswerCount () {
      return this.answersGiven.length
    },
    enforceLatinNumberSystem
  }
}
</script>
<style lang="scss" scoped>
  .playButton{
    outline:none;
    img {
      margin: 0 auto;
      width: 10em;
      display: block;
    }
  }
  .resultBox {
    font-family: var(--user-chosen-font);
  }

  .resultBox p {
    font-size: 1.2em;
  }

  .answersList {
    list-style-type: none;
    columns: 2;
    -webkit-columns: 2;
    -moz-columns: 2;
  }

  .answersList .correct {
    color: green;
  }

  .answersList .incorrect {
    color: hsl(348, 100%, 61%);
  }
</style>
